import React, { useRef, useState, useEffect, useCallback } from "react";
import "./Video_Player.css";

function VideoPlayer({ src }) {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const volumeRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [currentTime, setCurrentTime] = useState("0:00");
  const [duration, setDuration] = useState("0:00");
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isEnded, setIsEnded] = useState(false);

  const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const hrs = Math.floor(time / 3600);
    const mins = Math.floor((time % 3600) / 60);
    const secs = Math.floor(time % 60);
    if (hrs > 0) return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    clearTimeout(controlsTimeoutRef.current);
    if (isPlaying && !isDragging && !showSpeedMenu && !showVolumeSlider) {
      controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isPlaying, isDragging, showSpeedMenu, showVolumeSlider]);

  useEffect(() => {
    resetControlsTimer();
    return () => clearTimeout(controlsTimeoutRef.current);
  }, [resetControlsTimer]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(formatTime(video.currentTime));
    setDuration(formatTime(video.duration));
    setProgress((video.currentTime / video.duration) * 100 || 0);
    if (video.buffered.length > 0) {
      setBuffered((video.buffered.end(video.buffered.length - 1) / video.duration) * 100 || 0);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setIsEnded(true);
    setShowControls(true);
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isEnded) {
      video.currentTime = 0;
      setIsEnded(false);
    }
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const skip = (seconds) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(Math.max(video.currentTime + seconds, 0), video.duration);
  };

  const getProgressPosition = (e) => {
    const bar = e.currentTarget || e.target;
    const rect = bar.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    return Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
  };

  const seek = (e) => {
    const video = videoRef.current;
    if (!video) return;
    const pos = getProgressPosition(e);
    video.currentTime = pos * video.duration;
  };

  const handleProgressMouseDown = (e) => {
    setIsDragging(true);
    seek(e);
  };

  const handleProgressMouseMove = (e) => {
    if (!isDragging) return;
    seek(e);
  };

  const handleProgressMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleProgressMouseMove);
      window.addEventListener("mouseup", handleProgressMouseUp);
      window.addEventListener("touchmove", handleProgressMouseMove);
      window.addEventListener("touchend", handleProgressMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleProgressMouseMove);
      window.removeEventListener("mouseup", handleProgressMouseUp);
      window.removeEventListener("touchmove", handleProgressMouseMove);
      window.removeEventListener("touchend", handleProgressMouseUp);
    };
  }, [isDragging]);

  const handleVolumeChange = (e) => {
    const val = parseFloat(e.target.value);
    videoRef.current.volume = val;
    setVolume(val);
    setIsMuted(val === 0);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    if (isMuted) {
      video.volume = volume || 0.7;
      setIsMuted(false);
    } else {
      video.volume = 0;
      setIsMuted(true);
    }
  };

  const setSpeed = (rate) => {
    videoRef.current.playbackRate = rate;
    setPlaybackRate(rate);
    setShowSpeedMenu(false);
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!document.fullscreenElement) {
      container.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return "🔇";
    if (volume < 0.4) return "🔈";
    if (volume < 0.7) return "🔉";
    return "🔊";
  };

  return (
    <div
      ref={containerRef}
      className={`vp-container ${showControls ? "vp-show-controls" : ""} ${isFullscreen ? "vp-fullscreen" : ""}`}
      onMouseMove={resetControlsTimer}
      onTouchStart={resetControlsTimer}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      onClick={(e) => {
        if (e.target === videoRef.current || e.target === containerRef.current) togglePlay();
      }}
    >
      <video
        ref={videoRef}
        src={src}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        className="vp-video"
        controlsList="nodownload"
        onContextMenu={(e) => e.preventDefault()}
        playsInline
      />

      <div className={`vp-center-overlay ${!isPlaying || isEnded ? "vp-center-visible" : ""}`}>
        <button className="vp-center-btn" onClick={togglePlay} aria-label={isEnded ? "Replay" : isPlaying ? "Pause" : "Play"}>
          <span className="vp-center-icon">{isEnded ? "↺" : isPlaying ? "⏸" : "▶"}</span>
        </button>
      </div>

      <div className="vp-controls">
        <div
          className="vp-progress-wrapper"
          onMouseDown={handleProgressMouseDown}
          onTouchStart={(e) => { setIsDragging(true); seek(e); }}
          role="slider"
          aria-label="Video progress"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") skip(5);
            if (e.key === "ArrowLeft") skip(-5);
          }}
        >
          <div className="vp-progress-track">
            <div className="vp-progress-buffered" style={{ width: `${buffered}%` }} />
            <div className="vp-progress-fill" style={{ width: `${progress}%` }}>
              <div className="vp-progress-thumb" />
            </div>
          </div>
        </div>

        <div className="vp-btn-row">
          <div className="vp-btn-group">
            <button className="vp-btn" onClick={() => skip(-10)} title="Rewind 10s" aria-label="Rewind 10 seconds">
              <span className="vp-btn-icon">⏮</span>
              <span className="vp-btn-label">10</span>
            </button>

            <button className="vp-btn vp-btn-play" onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
              {isEnded ? "↺" : isPlaying ? "⏸" : "▶"}
            </button>

            <button className="vp-btn" onClick={() => skip(10)} title="Forward 10s" aria-label="Forward 10 seconds">
              <span className="vp-btn-icon">⏭</span>
              <span className="vp-btn-label">10</span>
            </button>

            <div
              className="vp-volume-wrapper"
              onMouseEnter={() => setShowVolumeSlider(true)}
              onMouseLeave={() => setShowVolumeSlider(false)}
            >
              <button className="vp-btn" onClick={toggleMute} aria-label="Toggle mute">
                {getVolumeIcon()}
              </button>
              <div className={`vp-volume-slider-container ${showVolumeSlider ? "vp-volume-visible" : ""}`} ref={volumeRef}>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="vp-volume-slider"
                  aria-label="Volume"
                />
              </div>
            </div>

            <span className="vp-time">{currentTime} <span className="vp-time-sep">/</span> {duration}</span>
          </div>

          <div className="vp-btn-group">
            <div className="vp-speed-wrapper">
              <button
                className="vp-btn vp-btn-speed"
                onClick={() => setShowSpeedMenu((s) => !s)}
                aria-label="Playback speed"
              >
                {playbackRate}×
              </button>
              {showSpeedMenu && (
                <div className="vp-speed-menu" role="menu">
                  {SPEEDS.map((rate) => (
                    <button
                      key={rate}
                      className={`vp-speed-option ${playbackRate === rate ? "vp-speed-active" : ""}`}
                      onClick={() => setSpeed(rate)}
                      role="menuitem"
                    >
                      {rate}×
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button className="vp-btn" onClick={toggleFullscreen} aria-label="Toggle fullscreen">
              {isFullscreen ? "⛶" : "⛶"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoPlayer;